import React, { useEffect, useState } from "react";
import {
  ShieldCheck, Target, Brain, Bell, ArrowsDownUp, ArrowRight,
  Star, Users, Crown, CheckCircle, Lightning, Lock, Sparkle,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";

/** Compact sparkline for the top winner cards. */
function MiniSpark({ values = [], positive = true }) {
  if (!values || values.length < 2) {
    // Fallback synthetic upward curve
    values = [0, 2, 1, 4, 3, 6, 8, 7, 10, 12, 11, 14];
  }
  const W = 100, H = 28, PAD = 2;
  const minY = Math.min(...values);
  const maxY = Math.max(...values);
  const range = maxY - minY || 1;
  const stepX = (W - PAD * 2) / (values.length - 1);
  const toY = (v) => PAD + (1 - (v - minY) / range) * (H - PAD * 2);
  const path = values.map((v, i) => `${i === 0 ? "M" : "L"} ${PAD + i * stepX} ${toY(v)}`).join(" ");
  const color = positive ? "hsl(var(--pos))" : "hsl(var(--neg))";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-7" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`mspark-${positive ? "p" : "n"}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${path} L ${W - PAD} ${H} L ${PAD} ${H} Z`} fill={`url(#mspark-${positive ? "p" : "n"})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const HERO_FEATURES = [
  { icon: ShieldCheck, label: "Volume-Confirmed Breakouts", color: "text-pos" },
  { icon: Target, label: "Auto Entry / SL / Target", color: "text-pos" },
  { icon: Brain, label: "AI Fundamental Verdict", color: "text-info" },
  { icon: Bell, label: "Instant SL / Target Alerts", color: "text-mod" },
  { icon: ArrowsDownUp, label: "Sector Top Movers", color: "text-pos" },
];

function HeroBadgeRow() {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="hero-feature-badges">
      {HERO_FEATURES.map((f) => {
        const Icon = f.icon;
        return (
          <span
            key={f.label}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-subtle rounded-sm bg-surface/60 text-[10px] font-mono uppercase tracking-widest text-secondary-fg"
          >
            <Icon size={11} weight="fill" className={f.color} />
            {f.label}
          </span>
        );
      })}
    </div>
  );
}

function JoinedBadge({ traders }) {
  // Animate a small "joining" pulse for visual liveness — does NOT inflate
  // the actual count. Number always comes from real-users-floored backend value.
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    const id = setInterval(() => {
      setPulse(true);
      setTimeout(() => setPulse(false), 600);
    }, 8000);
    return () => clearInterval(id);
  }, []);
  if (traders == null) return null;
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 border border-pos/40 rounded-sm bg-pos-soft/15"
      data-testid="joined-badge"
    >
      <span className="relative flex h-2 w-2">
        <span className={"absolute inline-flex h-full w-full rounded-full bg-pos opacity-75 " + (pulse ? "animate-ping" : "")} />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-pos" />
      </span>
      <Users size={12} weight="fill" className="text-pos" />
      <span className="text-[11px] font-mono text-secondary-fg">
        <span className="text-primary-fg tabular-nums font-semibold">
          {traders.toLocaleString("en-IN")}+
        </span>{" "}
        traders joined
      </span>
    </div>
  );
}

function SpotlightStockCard() {
  const [sig, setSig] = useState(null);
  useEffect(() => {
    api.get("/public/sample-signal").then(({ data }) => setSig(data)).catch(() => {});
  }, []);
  if (!sig) return null;
  const rr = (sig.entry && sig.stop_loss && sig.target)
    ? Math.abs(sig.target - sig.entry) / Math.abs(sig.entry - sig.stop_loss)
    : null;
  const score = Math.min(99, Math.max(60, Math.round(75 + (rr || 1.5) * 6)));
  return (
    <div className="border border-pos/40 bg-pos-soft/10 rounded-sm p-4" data-testid="spotlight-stock">
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-pos">
          <Sparkle size={11} weight="fill" />
          Strong Breakout · AI Score
        </div>
        <div className="px-2 py-0.5 rounded-sm bg-pos text-black text-xs font-mono font-bold tabular-nums">
          {score}
        </div>
      </div>
      <div className="text-xl font-medium text-primary-fg leading-tight">{sig.symbol}</div>
      <div className="text-[11px] text-muted-fg mb-3 truncate">{sig.company_name}</div>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="font-mono tabular-nums text-2xl text-primary-fg">
          ₹{(sig.last_price ?? sig.entry)?.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
        </span>
        {typeof sig.pct_change_today === "number" && (
          <span className={
            "text-[11px] font-mono tabular-nums " +
            (sig.pct_change_today >= 0 ? "text-pos" : "text-neg")
          }>
            {sig.pct_change_today >= 0 ? "+" : ""}{sig.pct_change_today.toFixed(2)}% today
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 text-[10px] font-mono uppercase tracking-widest">
        <div className="text-muted-fg">
          Entry
          <div className="text-primary-fg text-sm tabular-nums">₹{Number(sig.entry).toFixed(0)}</div>
        </div>
        <div className="text-muted-fg">
          SL
          <div className="text-neg text-sm tabular-nums">₹{Number(sig.stop_loss).toFixed(0)}</div>
        </div>
        <div className="text-muted-fg">
          Target
          <div className="text-pos text-sm tabular-nums">₹{Number(sig.target).toFixed(0)}</div>
        </div>
      </div>
      <div className="mt-3 text-[9px] text-muted-fg/70 italic">
        Rotates daily · 100+ similar setups inside the dashboard.
      </div>
    </div>
  );
}

function MiniTrackRecord({ stats }) {
  if (!stats) return null;
  const w = stats.last_30d || {};
  return (
    <div className="border border-subtle rounded-sm p-4 bg-surface/60" data-testid="mini-track-record">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-3">
        Last 30 days · Live track record
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">Win rate</div>
          <div className="text-lg font-mono tabular-nums text-primary-fg">
            {(w.win_rate ?? 0).toFixed(0)}<span className="text-xs text-muted-fg">%</span>
          </div>
          <div className="text-[9px] text-muted-fg">{w.wins ?? 0}W · {w.losses ?? 0}L</div>
        </div>
        <div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">Avg win</div>
          <div className="text-lg font-mono tabular-nums text-pos">
            +{(w.avg_pnl_pct ?? 0).toFixed(1)}%
          </div>
          <div className="text-[9px] text-muted-fg">on target hits</div>
        </div>
        <div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">Best win</div>
          <div className="text-lg font-mono tabular-nums text-pos">
            +{(w.best_win_pct ?? 0).toFixed(1)}%
          </div>
          <div className="text-[9px] text-muted-fg">single trade</div>
        </div>
      </div>
      {Array.isArray(w.top_3_wins) && w.top_3_wins.length > 0 && (
        <div className="pt-3 border-t border-subtle">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-2">Top 3 winners</div>
          <div className="space-y-1.5">
            {w.top_3_wins.map((it) => (
              <div key={it.symbol} className="flex items-center gap-2" data-testid={`top-winner-${it.symbol}`}>
                <div className="text-xs font-mono text-primary-fg w-24 truncate">{it.symbol}</div>
                <div className="flex-1">
                  <MiniSpark positive={(it.pnl_pct || 0) >= 0} />
                </div>
                <div className={"text-xs font-mono tabular-nums " + ((it.pnl_pct || 0) >= 0 ? "text-pos" : "text-neg")}>
                  {(it.pnl_pct || 0) >= 0 ? "+" : ""}{(it.pnl_pct || 0).toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TestimonialCard({ t }) {
  return (
    <div
      className="border border-subtle rounded-sm p-4 bg-surface/60 hover:border-pos/40 transition"
      data-testid={`testimonial-${t.id}`}
    >
      <div className="flex items-center gap-1 mb-3">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={12}
            weight="fill"
            className={i < (t.rating || 5) ? "text-mod" : "text-subtle"}
          />
        ))}
      </div>
      <p className="text-[13px] text-secondary-fg leading-relaxed mb-3 italic">
        “{t.quote}”
      </p>
      <div className="flex items-center gap-2">
        {t.avatar_url ? (
          <img
            src={t.avatar_url}
            alt={t.name}
            className="w-8 h-8 rounded-full border border-subtle object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full border border-subtle bg-surface flex items-center justify-center text-xs font-mono text-muted-fg">
            {(t.name?.[0] || "?").toUpperCase()}
          </div>
        )}
        <div>
          <div className="text-xs font-medium text-primary-fg">{t.name}</div>
          {t.role && <div className="text-[10px] text-muted-fg">{t.role}</div>}
        </div>
      </div>
    </div>
  );
}

function Testimonials() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/testimonials").then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, []);
  if (items.length === 0) return null;     // hide entire section when admin hasn't added any
  return (
    <div className="space-y-3" data-testid="testimonials-section">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg flex items-center gap-2">
        <Star size={11} weight="fill" className="text-mod" />
        What our users say
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.slice(0, 4).map((t) => <TestimonialCard key={t.id} t={t} />)}
      </div>
    </div>
  );
}

function TrialCtaBanner() {
  return (
    <div
      className="rounded-sm p-5 sm:p-6 border border-pos shadow-[0_0_40px_-10px_rgba(40,255,180,0.5)] relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, hsl(160 84% 14%) 0%, hsl(160 84% 22%) 100%)" }}
      data-testid="trial-cta-banner"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: "linear-gradient(rgba(40,255,180,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(40,255,180,0.15) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      <div className="relative flex items-start gap-3 mb-4">
        <Crown size={20} weight="fill" className="text-mod shrink-0 mt-0.5" />
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-pos mb-1">
            Limited Time Offer
          </div>
          <div className="text-lg sm:text-xl font-medium text-white leading-tight">
            Try full premium access for just <span className="text-pos">₹5</span> for 5 days
          </div>
          <div className="text-[11px] text-pos/80 mt-1">
            No commitment · cancel anytime from your account
          </div>
        </div>
      </div>
      <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {[
          { icon: CheckCircle, label: "Full Access" },
          { icon: Bell, label: "Real-time Alerts" },
          { icon: ArrowRight, label: "Cancel Anytime" },
          { icon: Lock, label: "Secure Payments" },
        ].map(({ icon: Ic, label }) => (
          <div key={label} className="flex items-center gap-1.5 text-[10px] text-pos font-mono uppercase tracking-widest">
            <Ic size={11} weight="bold" />
            {label}
          </div>
        ))}
      </div>
      <Link
        to="/register"
        data-testid="trial-cta-btn"
        onClick={() => {
          // Force-scroll the AuthForm into view even when URL doesn't change
          // (e.g. when this CTA is clicked while already on /register).
          setTimeout(() => {
            const anchor = document.getElementById("auth-form-anchor");
            if (anchor) anchor.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 120);
        }}
        className="group relative inline-flex items-center gap-2.5 px-6 py-3.5 rounded-sm text-base sm:text-lg font-bold uppercase tracking-wide text-black bg-pos shadow-[0_0_30px_-4px_rgba(40,255,180,0.7)] hover:shadow-[0_0_40px_-2px_rgba(40,255,180,0.95)] hover:scale-[1.02] active:scale-[0.99] transition-all duration-200 ring-2 ring-pos/40 hover:ring-pos focus:outline-none focus-visible:ring-4 focus-visible:ring-pos"
        style={{ boxShadow: "0 0 24px hsla(160, 84%, 50%, 0.55)" }}
      >
        Start 5-day trial at ₹5
        <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  );
}

/**
 * Landing-page showcase — composed of:
 *   - Hero feature badges
 *   - Joined-traders proof
 *   - Stock spotlight (rotates daily)
 *   - Mini 30-day track record + top winners with sparklines
 *   - User testimonials (admin-managed, hidden when empty)
 *   - ₹5 / 5-day trial CTA banner
 */
export default function LandingShowcase() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  return (
    <section className="space-y-5" data-testid="landing-showcase">
      <div className="flex flex-wrap items-center gap-3">
        <HeroBadgeRow />
        <div className="ml-auto"><JoinedBadge traders={stats?.traders} /></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.1fr] gap-4">
        <SpotlightStockCard />
        <MiniTrackRecord stats={stats} />
      </div>

      <Testimonials />

      <TrialCtaBanner />
    </section>
  );
}
